/**
 * Bag Analyzer - Problem Detection Engine
 * 
 * Analyzes user's golf bag to identify specific problems:
 * - Gapping issues (gaps > 15 yards between clubs)
 * - Outdated equipment (clubs older than 5-7 years)
 * - Missing coverage (no club for common distances)
 * - Weak spots (clubs with inconsistent performance)
 */

import { UserProfile, GolfClub, ClubType } from '../types';

export type ProblemSeverity = 'critical' | 'high' | 'medium' | 'low';
export type ProblemType = 'gapping' | 'outdated' | 'missing-coverage' | 'weak-spot' | 'set-optimization';

export interface BagProblem {
  id: string;
  type: ProblemType;
  severity: ProblemSeverity;
  title: string;
  description: string;
  impact: string; // e.g., "Costs ~2 strokes per round"
  affectedClubs?: GolfClub[];
  affectedRange?: {
    from: number;
    to: number;
  };
  suggestedClubType?: ClubType;
  // For solution generation
  solutionHint: string;
  // Gap-specific rules for recommendations
  gapRules?: {
    specificSuggestion: string;
    validSolutions: string[];
    forbidden: string[];
    adjustmentFirst: string;
  };
  // AI-enhanced fields (populated by aiService.getAIBagAnalysis)
  aiPriority?: number;
  aiReason?: string;
}

export interface BagHealthReport {
  overallScore: number; // 0-100
  problems: BagProblem[];
  strengths: string[];
  summary: string;
  // AI-enhanced fields (populated by aiService.getAIBagAnalysis)
  aiInsights?: string[];
  priorityRecommendation?: string;
  scoringImpact?: string;
  analyzedAt?: Date;
}

// Standard club order for gapping analysis
const CLUB_ORDER: ClubType[] = [
  'driver', '3-wood', '5-wood', '7-wood',
  '2-hybrid', '3-hybrid', '4-hybrid', '5-hybrid',
  '3-iron', '4-iron', '5-iron', '6-iron', '7-iron', '8-iron', '9-iron',
  'pw', 'gw', 'aw', 'sw', 'lw'
];

// Expected distances by club type (average amateur golfer)
// Used for estimating distances when user hasn't provided them
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EXPECTED_DISTANCES: Partial<Record<ClubType, number>> = {
  'driver': 220,
  '3-wood': 200,
  '5-wood': 185,
  '7-wood': 175,
  '3-hybrid': 180,
  '4-hybrid': 170,
  '5-hybrid': 160,
  '5-iron': 155,
  '6-iron': 145,
  '7-iron': 135,
  '8-iron': 125,
  '9-iron': 115,
  'pw': 105,
  'gw': 95,
  'sw': 85,
  'lw': 70
};

// Typical distance ranges for each club type (amateur golfers)
// Used to validate that a suggested gap-filler can realistically cover the target distance
const CLUB_DISTANCE_RANGES: Partial<Record<ClubType, { min: number; max: number }>> = {
  'driver': { min: 190, max: 280 },
  '3-wood': { min: 170, max: 240 },
  '5-wood': { min: 155, max: 215 },
  '7-wood': { min: 145, max: 200 },
  '2-hybrid': { min: 160, max: 210 },
  '3-hybrid': { min: 150, max: 200 },
  '4-hybrid': { min: 140, max: 190 },
  '5-hybrid': { min: 130, max: 180 },
  '3-iron': { min: 150, max: 200 },
  '4-iron': { min: 140, max: 185 },
  '5-iron': { min: 130, max: 175 },
  '6-iron': { min: 120, max: 165 },
  '7-iron': { min: 110, max: 155 },
  '8-iron': { min: 100, max: 140 },
  '9-iron': { min: 90, max: 130 },
  'pw': { min: 80, max: 120 },
  'gw': { min: 70, max: 110 },
  'aw': { min: 65, max: 105 },
  'sw': { min: 55, max: 95 },
  'lw': { min: 40, max: 75 },
};

/**
 * Validates whether a suggested club can realistically cover the target distance.
 * Returns true if the club's typical range overlaps with the target carry.
 */
function isClubViableForDistance(clubType: ClubType, targetCarry: number): boolean {
  const range = CLUB_DISTANCE_RANGES[clubType];
  if (!range) return true; // Unknown club type, allow it
  // Allow a 15-yard buffer on either side for individual variation
  return targetCarry >= range.min - 15 && targetCarry <= range.max + 15;
}

// Critical scoring zones that should have coverage
const SCORING_ZONES = [
  { name: 'Approach', from: 100, to: 150, priority: 'high' as const },
  { name: 'Mid-range', from: 150, to: 180, priority: 'high' as const },
  { name: 'Long approach', from: 180, to: 210, priority: 'medium' as const },
  { name: 'Fairway', from: 210, to: 240, priority: 'medium' as const }
];

/**
 * CLUB_ADJACENCY_RULES - Explicit rules for what club types are allowed/forbidden for each gap scenario
 * 
 * Key format: "upperCategory|lowerCategory" where category is derived from club type
 * Each rule specifies:
 * - validSolutions: Club types that CAN fill this gap
 * - forbidden: Club types that should NEVER be suggested
 * - adjustmentFirst: Suggestion to try before buying new clubs
 * - specificSuggestion: The most common solution for this gap type
 */
export interface GapRule {
  validSolutions: string[];
  forbidden: string[];
  adjustmentFirst: string;
  specificSuggestion: string;
}

export const CLUB_ADJACENCY_RULES: Record<string, GapRule> = {
  // Driver to 3-Wood: NO intermediate club exists - only adjustments or strong 3W
  'driver|wood': {
    validSolutions: ['strong-3-wood', 'mini-driver'],
    forbidden: ['driver', '5-wood', '7-wood', 'hybrid', 'iron', 'wedge'],
    adjustmentFirst: 'Lower your 3-wood loft to 13-13.5°, or adjust driver loft up. A mini-driver (12-13°) is the only new club option.',
    specificSuggestion: 'Strong 3-Wood (13-14°) or Mini-Driver'
  },
  
  // 3-Wood to 5-Wood: Suggest 4-wood or 7-wood, NOT another 3-wood
  'wood|wood': {
    validSolutions: ['4-wood', '7-wood', '9-wood'],
    forbidden: ['driver', 'hybrid', 'iron', 'wedge'],
    adjustmentFirst: 'Check loft gaps between your woods - consider bending lofts or different shafts.',
    specificSuggestion: '7-Wood or 4-Wood'
  },
  
  // Wood to Hybrid: Suggest high-loft wood or low-numbered hybrid
  'wood|hybrid': {
    validSolutions: ['7-wood', '9-wood', '2-hybrid', '3-hybrid'],
    forbidden: ['driver', '3-wood', '5-wood', 'iron', 'wedge'],
    adjustmentFirst: 'A 7-wood often fills this gap better than adding another hybrid.',
    specificSuggestion: '7-Wood or 2-Hybrid'
  },
  
  // Hybrid to Hybrid: ONLY intermediate hybrid (e.g., 4H for 3H-5H gap)
  'hybrid|hybrid': {
    validSolutions: ['intermediate-hybrid'],
    forbidden: ['driver', 'wood', 'iron', 'wedge'],
    adjustmentFirst: 'Adjust lofts on existing hybrids, or swap one for a different loft.',
    specificSuggestion: 'Intermediate Hybrid (e.g., 4H for 3H-5H gap)'
  },
  
  // Hybrid to Iron: Utility/driving iron ONLY - NOT a higher-numbered hybrid
  // Example: Gap between 5-Hybrid and 5-Iron should be filled by utility iron, NOT 4-Hybrid
  // A 4-Hybrid sits ABOVE the 5-Hybrid (longer), not between 5H and 5I
  'hybrid|iron': {
    validSolutions: ['utility-iron', 'driving-iron', 'strong-lofted-iron'],
    forbidden: ['driver', 'wood', 'wedge', 'higher-hybrid'],
    adjustmentFirst: 'A utility iron or driving iron is the ONLY solution for hybrid-to-iron gaps. Adjusting lofts on your existing hybrid or iron may also help.',
    specificSuggestion: 'Utility Iron or Driving Iron'
  },
  
  // Iron to Iron: Only intermediate iron
  'iron|iron': {
    validSolutions: ['iron'],
    forbidden: ['driver', 'wood', 'hybrid', 'wedge'],
    adjustmentFirst: 'Your iron set may need re-gapping - check lofts with a fitter.',
    specificSuggestion: 'Intermediate Iron'
  },
  
  // Iron to Wedge: Gap wedge or pitching wedge
  'iron|wedge': {
    validSolutions: ['pw', 'gw', '48-wedge', '50-wedge'],
    forbidden: ['driver', 'wood', 'hybrid'],
    adjustmentFirst: 'Check your PW loft - modern PW lofts vary widely (43-47°).',
    specificSuggestion: 'Gap Wedge (48-52°)'
  },
  
  // Wedge to Wedge: Intermediate wedge loft
  'wedge|wedge': {
    validSolutions: ['gw', 'aw', 'sw', 'lw', '50-wedge', '52-wedge', '54-wedge', '56-wedge', '58-wedge', '60-wedge'],
    forbidden: ['driver', 'wood', 'hybrid', 'iron'],
    adjustmentFirst: 'Check your wedge lofts - aim for 4° gaps between each wedge.',
    specificSuggestion: 'Intermediate Wedge (4° gap between each)'
  }
};

/**
 * Get the category of a club type for adjacency rule lookup
 */
export function getClubCategory(clubType: ClubType | string): 'driver' | 'wood' | 'hybrid' | 'iron' | 'wedge' {
  if (clubType === 'driver') return 'driver';
  if (clubType.includes('wood')) return 'wood';
  if (clubType.includes('hybrid')) return 'hybrid';
  if (['pw', 'gw', 'aw', 'sw', 'lw'].includes(clubType) || clubType.includes('wedge')) return 'wedge';
  if (clubType.includes('iron')) return 'iron';
  // Default fallback
  return 'iron';
}

/**
 * Get the adjacency rule for a gap between two clubs
 */
export function getGapRule(upperClub: ClubType | string, lowerClub: ClubType | string): GapRule | null {
  const upperCategory = getClubCategory(upperClub);
  const lowerCategory = getClubCategory(lowerClub);
  
  // Try exact match first
  const exactKey = `${upperCategory}|${lowerCategory}`;
  if (CLUB_ADJACENCY_RULES[exactKey]) {
    return CLUB_ADJACENCY_RULES[exactKey];
  }
  
  // For driver gaps, always use driver|wood rule
  if (upperCategory === 'driver') {
    return CLUB_ADJACENCY_RULES['driver|wood'];
  }
  
  return null;
}

/**
 * Analyze a user's bag and return a health report with specific problems
 */
export function analyzeBag(userProfile: UserProfile): BagHealthReport {
  const problems: BagProblem[] = [];
  const strengths: string[] = [];
  
  const bag = userProfile.golfBag || [];
  const legacyDistances = userProfile.clubDistances || [];
  
  // Build a map of club -> distance
  // PRIORITY: golfBag is the AUTHORITATIVE source - it contains the user's current data
  // legacyDistances (from onboarding) is ONLY used as fallback for clubs without golfBag distances
  const distanceMap = new Map<ClubType, number>();
  
  // For each club in golfBag, use golfBag distance if available
  // Otherwise, check if legacyDistances has a value as fallback
  // EXCLUDE PUTTER - it's not a distance club and should never be in gapping/coverage analysis
  bag.forEach(club => {
    if (club.type === 'putter') return; // Skip putter entirely
    
    if (club.distance && club.distance > 0) {
      // Use authoritative golfBag distance
      distanceMap.set(club.type, club.distance);
    } else {
      // No golfBag distance - check legacy as fallback only
      const legacy = legacyDistances.find(d => d.club === club.type);
      if (legacy?.distance && legacy.distance > 0) {
        distanceMap.set(club.type, legacy.distance);
      }
      // If neither has a distance, don't add to map (club has no distance data)
    }
  });
  
  // Note: We intentionally do NOT add clubs from legacyDistances that aren't in golfBag
  // The golfBag is the definitive list of what clubs the user actually has

  // 1. Analyze Gapping Issues
  const gappingProblems = analyzeGapping(bag, distanceMap);
  problems.push(...gappingProblems);

  // 2. Analyze Missing Coverage
  const coverageProblems = analyzeCoverage(bag, distanceMap);
  problems.push(...coverageProblems);

  // 3. Analyze Set Optimization
  const optimizationProblems = analyzeSetOptimization(bag, userProfile);
  problems.push(...optimizationProblems);

  // 4. Analyze Weak Spots (based on user's current issues)
  const weakSpotProblems = analyzeWeakSpots(bag, userProfile);
  problems.push(...weakSpotProblems);

  // Calculate strengths
  if (bag.length >= 12) {
    strengths.push('Complete bag with good club variety');
  }
  if (distanceMap.size >= 10) {
    strengths.push('Good distance data for accurate recommendations');
  }
  if (bag.filter(c => c.type.includes('wedge') || ['pw', 'gw', 'aw', 'sw', 'lw'].includes(c.type)).length >= 3) {
    strengths.push('Solid wedge coverage for scoring');
  }

  // Calculate overall score
  const criticalCount = problems.filter(p => p.severity === 'critical').length;
  const highCount = problems.filter(p => p.severity === 'high').length;
  const mediumCount = problems.filter(p => p.severity === 'medium').length;
  const lowCount = problems.filter(p => p.severity === 'low').length;
  
  let overallScore = 100;
  overallScore -= criticalCount * 20;
  overallScore -= highCount * 10;
  overallScore -= mediumCount * 5;
  overallScore -= lowCount * 2;
  overallScore = Math.max(0, Math.min(100, overallScore));

  // Generate summary
  let summary = '';
  if (problems.length === 0) {
    summary = 'Your bag is well-optimized! No significant issues detected.';
  } else if (criticalCount > 0) {
    summary = `Found ${criticalCount} critical issue${criticalCount > 1 ? 's' : ''} that could be costing you strokes.`;
  } else if (highCount > 0) {
    summary = `Found ${highCount} high-priority improvement${highCount > 1 ? 's' : ''} to optimize your game.`;
  } else {
    summary = `Your bag is in good shape with ${problems.length} minor optimization${problems.length > 1 ? 's' : ''} available.`;
  }

  // Sort problems by severity
  const severityOrder: Record<ProblemSeverity, number> = {
    'critical': 0,
    'high': 1,
    'medium': 2,
    'low': 3
  };
  problems.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return {
    overallScore,
    problems,
    strengths,
    summary
  };
}

/**
 * Analyze gapping issues between clubs
 * Now includes full gap rules with allowed/forbidden club types
 */
function analyzeGapping(bag: GolfClub[], distanceMap: Map<ClubType, number>): BagProblem[] {
  const problems: BagProblem[] = [];
  
  // Get clubs with distances, sorted by distance
  // EXCLUDE PUTTER - it's not a distance club and should never be part of gapping analysis
  const clubsWithDistance = bag
    .filter(club => distanceMap.has(club.type) && club.type !== 'putter')
    .map(club => ({ club, distance: distanceMap.get(club.type)! }))
    .sort((a, b) => b.distance - a.distance);

  if (clubsWithDistance.length < 2) return problems;

  // Check for gaps > 20 yards (critical) or > 15 yards (high)
  for (let i = 0; i < clubsWithDistance.length - 1; i++) {
    const current = clubsWithDistance[i];
    const next = clubsWithDistance[i + 1];
    const gap = current.distance - next.distance;

    if (gap > 20) {
      // Get full gap suggestion with rules
      const gapSuggestion = suggestGapFillerWithRules(current.club.type, next.club.type);
      const targetCarry = Math.round((current.distance + next.distance) / 2);
      
      // Distance-range validation: verify the suggested club can realistically hit the target distance
      let suggestion = gapSuggestion.specificSuggestion;
      if (!isClubViableForDistance(gapSuggestion.suggestedClub, targetCarry)) {
        // The suggested club's typical range doesn't match. Find a better fit from CLUB_ORDER.
        const betterFit = CLUB_ORDER.find(c => isClubViableForDistance(c, targetCarry) && c !== current.club.type && c !== next.club.type);
        if (betterFit) {
          suggestion = `${formatClubName(betterFit)} (tuned to ~${targetCarry} yds)`;
        }
      }
      
      problems.push({
        id: `gap-${current.club.type}-${next.club.type}`,
        type: 'gapping',
        severity: gap > 30 ? 'critical' : 'high',
        title: `${gap}-yard gap between ${formatClubName(current.club.type)} and ${formatClubName(next.club.type)}`,
        description: `No club covers ${next.distance + 10}-${current.distance - 10} yards. ${suggestion} recommended.`,
        impact: gap > 30 ? 'Costs ~2-3 strokes per round' : 'Costs ~1-2 strokes per round',
        affectedClubs: [current.club, next.club],
        affectedRange: {
          from: next.distance + 5,
          to: current.distance - 5
        },
        suggestedClubType: gapSuggestion.suggestedClub,
        gapRules: {
          specificSuggestion: suggestion,
          validSolutions: gapSuggestion.validSolutions,
          forbidden: gapSuggestion.forbidden,
          adjustmentFirst: gapSuggestion.adjustmentFirst
        },
        solutionHint: `${gapSuggestion.adjustmentFirst} Or add a ${suggestion} tuned to ${targetCarry} yards.`
      });
    } else if (gap > 15) {
      const gapSuggestion = suggestGapFillerWithRules(current.club.type, next.club.type);
      const targetCarry = Math.round((current.distance + next.distance) / 2);
      
      problems.push({
        id: `gap-${current.club.type}-${next.club.type}`,
        type: 'gapping',
        severity: 'medium',
        title: `${gap}-yard gap between ${formatClubName(current.club.type)} and ${formatClubName(next.club.type)}`,
        description: `Slightly large gap in ${next.distance + 5}-${current.distance - 5} yard range. ${gapSuggestion.specificSuggestion} could help.`,
        impact: 'May cost ~0.5-1 strokes per round',
        affectedClubs: [current.club, next.club],
        affectedRange: {
          from: next.distance + 5,
          to: current.distance - 5
        },
        suggestedClubType: gapSuggestion.suggestedClub,
        gapRules: {
          specificSuggestion: gapSuggestion.specificSuggestion,
          validSolutions: gapSuggestion.validSolutions,
          forbidden: gapSuggestion.forbidden,
          adjustmentFirst: gapSuggestion.adjustmentFirst
        },
        solutionHint: `${gapSuggestion.adjustmentFirst} Or consider a ${gapSuggestion.specificSuggestion} for ${targetCarry} yards.`
      });
    }
  }

  return problems;
}

/**
 * Analyze coverage for critical scoring zones
 */
function analyzeCoverage(bag: GolfClub[], distanceMap: Map<ClubType, number>): BagProblem[] {
  const problems: BagProblem[] = [];
  
  const distances = Array.from(distanceMap.values()).sort((a, b) => a - b);
  if (distances.length < 3) return problems;

  // Check each scoring zone
  for (const zone of SCORING_ZONES) {
    const clubsInZone = distances.filter(d => d >= zone.from && d <= zone.to);
    
    if (clubsInZone.length === 0) {
      problems.push({
        id: `coverage-${zone.name.toLowerCase().replace(' ', '-')}`,
        type: 'missing-coverage',
        severity: zone.priority === 'high' ? 'high' : 'medium',
        title: `No club coverage for ${zone.from}-${zone.to} yards`,
        description: `The ${zone.name.toLowerCase()} zone (${zone.from}-${zone.to} yards) has no dedicated club`,
        impact: zone.priority === 'high' ? 'Critical scoring zone uncovered' : 'May limit shot options',
        affectedRange: { from: zone.from, to: zone.to },
        solutionHint: `Add a club that carries ${Math.round((zone.from + zone.to) / 2)} yards`
      });
    }
  }

  return problems;
}

/**
 * Analyze set optimization opportunities
 */
function analyzeSetOptimization(bag: GolfClub[], userProfile: UserProfile): BagProblem[] {
  const problems: BagProblem[] = [];
  
  // Check for long irons that could be replaced with hybrids (for higher handicappers)
  const handicap = userProfile.handicap || 18;
  const longIrons = bag.filter(c => ['3-iron', '4-iron', '5-iron'].includes(c.type));
  
  if (handicap > 15 && longIrons.length > 0) {
    problems.push({
      id: 'long-irons-to-hybrids',
      type: 'set-optimization',
      severity: 'medium',
      title: `Long irons may be hard to hit at ${handicap} handicap`,
      description: `${longIrons.map(c => formatClubName(c.type)).join(', ')} could be replaced with more forgiving hybrids`,
      impact: 'Could improve consistency by 15-20%',
      affectedClubs: longIrons,
      solutionHint: 'Consider hybrid replacements for easier launch and more forgiveness'
    });
  }

  // Check wedge setup
  const wedges = bag.filter(c => ['pw', 'gw', 'aw', 'sw', 'lw'].includes(c.type));
  if (wedges.length < 3) {
    problems.push({
      id: 'wedge-gaps',
      type: 'set-optimization',
      severity: 'high',
      title: 'Incomplete wedge setup',
      description: `Only ${wedges.length} wedge${wedges.length !== 1 ? 's' : ''} in bag - recommend 3-4 for proper distance control`,
      impact: 'Limited scoring zone coverage',
      solutionHint: 'Add gap wedge (50-52°) and/or sand wedge (54-56°) for better short game coverage'
    });
  }

  // Check if they have a driver
  const hasDriver = bag.some(c => c.type === 'driver');
  if (!hasDriver) {
    problems.push({
      id: 'missing-driver',
      type: 'missing-coverage',
      severity: 'high',
      title: 'No driver in bag',
      description: 'Missing the most important distance club',
      impact: 'Losing significant distance off the tee',
      solutionHint: 'Add a driver suited to your swing speed'
    });
  }

  return problems;
}

/**
 * Analyze weak spots based on user's reported issues
 */
function analyzeWeakSpots(bag: GolfClub[], userProfile: UserProfile): BagProblem[] {
  const problems: BagProblem[] = [];
  const issues = userProfile.currentIssues || [];
  const ballFlight = userProfile.ballFlight;

  // Ball flight issues
  if (ballFlight === 'slice' || ballFlight === 'hook') {
    const affectedClubs = bag.filter(c => 
      ['driver', '3-wood', '5-wood'].includes(c.type) || c.type.includes('iron')
    );
    
    problems.push({
      id: `ball-flight-${ballFlight}`,
      type: 'weak-spot',
      severity: 'high',
      title: `Consistent ${ballFlight} pattern detected`,
      description: `Your ${ballFlight} may be costing you accuracy and distance`,
      impact: 'Losing ~10-15 yards and fairway accuracy',
      affectedClubs: affectedClubs.slice(0, 3),
      solutionHint: ballFlight === 'slice' 
        ? 'Consider draw-bias clubs or offset designs'
        : 'Consider fade-bias or less offset designs'
    });
  }

  // Distance issues
  if (issues.includes('distance') || issues.includes('Distance')) {
    problems.push({
      id: 'distance-issue',
      type: 'weak-spot',
      severity: 'medium',
      title: 'Distance optimization needed',
      description: 'You\'ve indicated distance is a concern',
      impact: 'May be leaving yards on the table',
      solutionHint: 'Look at shaft weight, loft optimization, and modern distance technology'
    });
  }

  // Consistency issues
  if (issues.includes('consistency') || issues.includes('Consistency')) {
    problems.push({
      id: 'consistency-issue',
      type: 'weak-spot',
      severity: 'medium',
      title: 'Consistency could be improved',
      description: 'Shot-to-shot consistency is a noted concern',
      impact: 'Inconsistent distances make club selection harder',
      solutionHint: 'Consider more forgiving club designs with larger sweet spots'
    });
  }

  return problems;
}

/**
 * Rich gap filler suggestion with adjacency rules
 */
export interface GapFillerSuggestion {
  suggestedClub: ClubType;
  clubCategory: 'driver' | 'wood' | 'hybrid' | 'iron' | 'wedge';
  specificSuggestion: string;  // e.g., "4 Hybrid" or "Strong 3-Wood (13-14°)"
  validSolutions: string[];
  forbidden: string[];
  adjustmentFirst: string;
  upperClub: ClubType;
  lowerClub: ClubType;
}

/**
 * Full gap filler suggestion with adjacency rules
 * This is the main function that enforces club eligibility
 */
export function suggestGapFillerWithRules(higherClub: ClubType, lowerClub: ClubType): GapFillerSuggestion {
  const higherIndex = CLUB_ORDER.indexOf(higherClub);
  const lowerIndex = CLUB_ORDER.indexOf(lowerClub);
  
  // Get the gap rule for this pair
  const gapRule = getGapRule(higherClub, lowerClub);
  const upperCategory = getClubCategory(higherClub);
  const lowerCategory = getClubCategory(lowerClub);
  
  // Default fallback
  const defaultResult: GapFillerSuggestion = {
    suggestedClub: '5-hybrid',
    clubCategory: 'hybrid',
    specificSuggestion: '5 Hybrid',
    validSolutions: ['hybrid'],
    forbidden: [],
    adjustmentFirst: 'Consider adjusting lofts on existing clubs first.',
    upperClub: higherClub,
    lowerClub: lowerClub
  };
  
  if (higherIndex === -1 || lowerIndex === -1) return defaultResult;
  
  let suggested: ClubType;
  let specificSuggestion: string;
  let clubCategory: 'driver' | 'wood' | 'hybrid' | 'iron' | 'wedge';
  
  // DRIVER TO WOOD GAP - Special case: no intermediate club exists
  if (higherClub === 'driver') {
    // Rule: NEVER suggest 5-wood for driver-to-3W gap
    // Only valid options: strong 3-wood, mini-driver, or loft adjustments
    suggested = '3-wood';  // We suggest 3-wood as the "club type" but with specific adjustment
    clubCategory = 'wood';
    specificSuggestion = 'Strong 3-Wood (13-14°) or Mini-Driver';
    
    return {
      suggestedClub: suggested,
      clubCategory,
      specificSuggestion,
      validSolutions: gapRule?.validSolutions || ['strong-3-wood', 'mini-driver'],
      forbidden: gapRule?.forbidden || ['driver', '5-wood', 'hybrid', 'iron'],
      adjustmentFirst: gapRule?.adjustmentFirst || 'Lower your 3-wood loft to 13-13.5°, or adjust driver loft up.',
      upperClub: higherClub,
      lowerClub: lowerClub
    };
  }
  
  // HYBRID TO HYBRID GAP - Must be intermediate hybrid
  if (upperCategory === 'hybrid' && lowerCategory === 'hybrid') {
    // Calculate the intermediate hybrid number
    const upperNum = parseInt(higherClub.replace('-hybrid', ''));
    const lowerNum = parseInt(lowerClub.replace('-hybrid', ''));
    const midNum = Math.round((upperNum + lowerNum) / 2);
    suggested = `${midNum}-hybrid` as ClubType;
    clubCategory = 'hybrid';
    specificSuggestion = `${midNum} Hybrid`;
    
    return {
      suggestedClub: suggested,
      clubCategory,
      specificSuggestion,
      validSolutions: gapRule?.validSolutions || ['intermediate-hybrid'],
      forbidden: gapRule?.forbidden || ['driver', 'wood', 'iron', 'wedge'],
      adjustmentFirst: gapRule?.adjustmentFirst || `Adjust your ${higherClub.replace('-', ' ')} loft up or ${lowerClub.replace('-', ' ')} loft down.`,
      upperClub: higherClub,
      lowerClub: lowerClub
    };
  }
  
  // HYBRID TO IRON GAP - UTILITY/DRIVING IRON ONLY
  // CRITICAL: A gap between 5-Hybrid and 5-Iron should NOT be filled by 4-Hybrid!
  // A 4-Hybrid sits ABOVE the 5-Hybrid (longer distance), creating overlap.
  // The correct solution is a utility iron or driving iron that transitions between hybrid and iron flight.
  if (upperCategory === 'hybrid' && lowerCategory === 'iron') {
    // DO NOT calculate a lower-numbered hybrid - that sits ABOVE the current hybrid
    // Instead, recommend utility iron / driving iron
    suggested = '4-iron' as ClubType; // Placeholder - actual recommendation is utility iron
    clubCategory = 'iron'; // Utility irons are iron-category
    specificSuggestion = 'Utility Iron or Driving Iron';
    
    const result = {
      suggestedClub: suggested,
      clubCategory,
      specificSuggestion,
      validSolutions: gapRule?.validSolutions || ['utility-iron', 'driving-iron', 'strong-lofted-iron'],
      forbidden: gapRule?.forbidden || ['driver', 'wood', 'wedge', 'higher-hybrid'],
      adjustmentFirst: gapRule?.adjustmentFirst || 'A utility iron or driving iron is the ONLY solution for hybrid-to-iron gaps. Adjusting lofts on your existing hybrid or iron may also help.',
      upperClub: higherClub,
      lowerClub: lowerClub
    };
    return result;
  }
  
  // IRON TO IRON GAP - Intermediate iron only
  if (upperCategory === 'iron' && lowerCategory === 'iron') {
    const upperNum = parseInt(higherClub.replace('-iron', ''));
    const lowerNum = parseInt(lowerClub.replace('-iron', ''));
    const midNum = Math.round((upperNum + lowerNum) / 2);
    suggested = `${midNum}-iron` as ClubType;
    clubCategory = 'iron';
    specificSuggestion = `${midNum} Iron`;
    
    return {
      suggestedClub: suggested,
      clubCategory,
      specificSuggestion,
      validSolutions: gapRule?.validSolutions || ['iron'],
      forbidden: gapRule?.forbidden || ['driver', 'wood', 'hybrid', 'wedge'],
      adjustmentFirst: gapRule?.adjustmentFirst || 'Your iron set may need re-gapping - check lofts with a fitter.',
      upperClub: higherClub,
      lowerClub: lowerClub
    };
  }
  
  // WEDGE GAPS
  if (upperCategory === 'wedge' || lowerCategory === 'wedge') {
    suggested = 'gw';
    clubCategory = 'wedge';
    specificSuggestion = 'Gap Wedge (50-52°)';
    
    return {
      suggestedClub: suggested,
      clubCategory,
      specificSuggestion,
      validSolutions: gapRule?.validSolutions || ['gw', 'aw', 'sw', 'lw'],
      forbidden: gapRule?.forbidden || ['driver', 'wood', 'hybrid', 'iron'],
      adjustmentFirst: gapRule?.adjustmentFirst || 'Check your wedge lofts - aim for 4° gaps between each.',
      upperClub: higherClub,
      lowerClub: lowerClub
    };
  }
  
  // WOOD TO WOOD or WOOD TO HYBRID - Use midpoint or 7-wood
  if (upperCategory === 'wood') {
    if (lowerCategory === 'wood') {
      suggested = '7-wood';
      clubCategory = 'wood';
      specificSuggestion = '7-Wood';
    } else {
      suggested = '7-wood';
      clubCategory = 'wood';
      specificSuggestion = '7-Wood or 9-Wood';
    }
    
    return {
      suggestedClub: suggested,
      clubCategory,
      specificSuggestion,
      validSolutions: gapRule?.validSolutions || ['7-wood', '9-wood'],
      forbidden: gapRule?.forbidden || ['driver', 'iron', 'wedge'],
      adjustmentFirst: gapRule?.adjustmentFirst || 'A 7-wood often fills this gap better than another hybrid.',
      upperClub: higherClub,
      lowerClub: lowerClub
    };
  }
  
  // FALLBACK: Use CLUB_ORDER midpoint
  if (lowerIndex - higherIndex <= 1) {
    suggested = lowerIndex < CLUB_ORDER.length - 1 ? CLUB_ORDER[lowerIndex] : '5-hybrid';
  } else {
    const midIndex = Math.floor((higherIndex + lowerIndex) / 2);
    suggested = midIndex === higherIndex 
      ? CLUB_ORDER[midIndex + 1] || '5-hybrid'
      : CLUB_ORDER[midIndex] || '5-hybrid';
  }
  
  clubCategory = getClubCategory(suggested);
  specificSuggestion = formatClubName(suggested);
  
  return {
    suggestedClub: suggested,
    clubCategory,
    specificSuggestion,
    validSolutions: gapRule?.validSolutions || [clubCategory],
    forbidden: gapRule?.forbidden || [],
    adjustmentFirst: gapRule?.adjustmentFirst || 'Consider adjusting lofts on existing clubs first.',
    upperClub: higherClub,
    lowerClub: lowerClub
  };
}

/**
 * Format club name for display
 */
function formatClubName(clubType: ClubType): string {
  const names: Partial<Record<ClubType, string>> = {
    'driver': 'Driver',
    '3-wood': '3-Wood',
    '5-wood': '5-Wood',
    '7-wood': '7-Wood',
    '2-hybrid': '2 Hybrid',
    '3-hybrid': '3 Hybrid',
    '4-hybrid': '4 Hybrid',
    '5-hybrid': '5 Hybrid',
    '3-iron': '3 Iron',
    '4-iron': '4 Iron',
    '5-iron': '5 Iron',
    '6-iron': '6 Iron',
    '7-iron': '7 Iron',
    '8-iron': '8 Iron',
    '9-iron': '9 Iron',
    'pw': 'Pitching Wedge',
    'gw': 'Gap Wedge',
    'aw': 'Approach Wedge',
    'sw': 'Sand Wedge',
    'lw': 'Lob Wedge',
    'putter': 'Putter'
  };
  return names[clubType] || clubType;
}

/**
 * Get problems filtered by severity
 */
export function getProblemsBySeverity(
  problems: BagProblem[], 
  severity: ProblemSeverity
): BagProblem[] {
  return problems.filter(p => p.severity === severity);
}

/**
 * Get the top N most impactful problems
 */
export function getTopProblems(problems: BagProblem[], limit: number = 3): BagProblem[] {
  return problems.slice(0, limit);
}

